import { call } from '@advanza/api'
import { ReclaimReason } from '@trustoo/types/entity/reclaimReason'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useList } from 'hooks/listHooks'
import { useEffect } from 'react'
import reclaimsSchema from 'schemes/reclaimsSchema'

type Options = {
    aboutMonth?: string
    countries?: string[]
    forceFetch?: boolean
    hidePendingAi?: boolean
    lastXMonths?: number
    leadType?: string
    noLimit?: boolean
    page?: number
    pageSize?: number
    providerId?: number
    query?: string
    reasonIds?: number[]
    serviceIds?: number[]
    status?: string
}

export function useReclaimsList(filterId: string, defaultOptions: Options) {
    return useList(
        filterId,
        {
            url: 'office/reclaims/get-list',
            schema: reclaimsSchema,
            reducerKey: 'reclaims',
        },
        defaultOptions,
    )
}

export function useReclaimReasons() {
    const dispatch = useAppDispatch()
    const { isFetchingReclaimReasons, reclaimReasons } = useAppSelector((state) => state.reclaims)

    useEffect(() => {
        if (!isFetchingReclaimReasons && !Object.keys(reclaimReasons).length) {
            dispatch({ type: 'REQUEST_RECLAIM_REASONS' })
            call('reclaims/get-reclaim-reasons/0/1').then(
                ({ reclaimReasons }: { reclaimReasons: Record<number, ReclaimReason> }) => {
                    dispatch({ type: 'RECEIVE_RECLAIM_REASONS', reclaimReasons })
                },
            )
        }
    })

    return { isFetchingReclaimReasons, reclaimReasons }
}

type ReclaimStatusCountsResponse = {
    reclaimStatusCounts: { accepted: number; denied: number; open: number }
}

export function useReclaimStatusCounts(providerId: number) {
    const dispatch = useAppDispatch()

    const { isFetchingReclaimStatusCounts, reclaimStatusCounts } = useAppSelector(
        (state) => state.reclaims,
    )

    useEffect(() => {
        if (providerId) {
            dispatch({ type: 'REQUEST_RECLAIM_STATUS_COUNTS' })
            call(`office/reclaims/get-reclaim-status-counts/${providerId}`)
                .then(({ reclaimStatusCounts }: ReclaimStatusCountsResponse) => {
                    dispatch({ type: 'RECEIVE_RECLAIM_STATUS_COUNTS', reclaimStatusCounts })
                })
                .catch(() => {
                    dispatch({ type: 'RECEIVE_RECLAIM_STATUS_COUNTS', reclaimStatusCounts: {} })
                })
        }
    }, [providerId, dispatch])

    return {
        isFetchingReclaimStatusCounts,
        reclaimStatusCounts,
    }
}
